/**
 * Authentication and User Session Store (LocalStorage Per-User Isolated Engine).
 * Ensures each user has their own isolated IndexedDB/LocalStorage workbook store,
 * custom databases, and workspace state that never deletes until explicit action.
 */

export interface UserProfile {
  email: string;
  name: string;
  avatar: string;
  isLoggedIn: boolean;
  token?: string;
}

export interface WorkbookMeta {
  id: string;
  name: string;
  owner: string;
  lastOpened: string;
  isReadOnly: boolean;
  isStarred: boolean;
  size: string;
  rowsCount: number;
}

const STORAGE_USER_KEY = 'numixsheet_active_user';
const STORAGE_WORKBOOKS_KEY = 'numixsheet_user_workbooks_';

const MEMORY_STORAGE = new Map<string, string>();

function getStorage(): { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void } {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
    if (typeof globalThis !== 'undefined' && 'localStorage' in globalThis && globalThis.localStorage) {
      return globalThis.localStorage;
    }
  } catch {
    // Storage access denied or blocked
  }
  return {
    getItem: (k: string) => MEMORY_STORAGE.get(k) ?? null,
    setItem: (k: string, v: string) => {
      MEMORY_STORAGE.set(k, v);
    },
    removeItem: (k: string) => {
      MEMORY_STORAGE.delete(k);
    },
  };
}

export function getActiveUser(): UserProfile | null {
  try {
    const raw = getStorage().getItem(STORAGE_USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UserProfile;
  } catch {
    return null;
  }
}

export function setActiveUser(user: UserProfile | null): void {
  const storage = getStorage();
  if (!user) {
    storage.removeItem(STORAGE_USER_KEY);
  } else {
    storage.setItem(STORAGE_USER_KEY, JSON.stringify(user));
  }
}

import { apiUrl } from './apiClient.js';

export async function logoutUser(): Promise<void> {
  setActiveUser(null);
  try {
    await fetch(apiUrl('/api/auth/email/logout'), { method: 'POST', credentials: 'same-origin' });
  } catch {
    // Best-effort logout network call
  }
}

export function getUserWorkbooks(email: string): WorkbookMeta[] {
  try {
    const key = `${STORAGE_WORKBOOKS_KEY}${email}`;
    const raw = getStorage().getItem(key);
    if (!raw) {
      // Clean fresh workspace for new user
      const defaults: WorkbookMeta[] = [];
      getStorage().setItem(key, JSON.stringify(defaults));
      return defaults;
    }
    return JSON.parse(raw) as WorkbookMeta[];
  } catch {
    return [];
  }
}

export function saveUserWorkbook(email: string, meta: WorkbookMeta): void {
  const key = `${STORAGE_WORKBOOKS_KEY}${email}`;
  const existing = getUserWorkbooks(email);
  const idx = existing.findIndex((w) => w.id === meta.id);
  if (idx >= 0) {
    existing[idx] = meta;
  } else {
    existing.unshift(meta);
  }
  getStorage().setItem(key, JSON.stringify(existing));
}

export function deleteUserWorkbook(email: string, id: string): void {
  const key = `${STORAGE_WORKBOOKS_KEY}${email}`;
  const existing = getUserWorkbooks(email).filter((w) => w.id !== id);
  getStorage().setItem(key, JSON.stringify(existing));
}

export interface DbConnectionMeta {
  id: string;
  name: string;
  engine: string;
  host: string;
  port: number;
  dbName: string;
  username: string;
  status: 'active' | 'testing' | 'error';
  latencyMs: number;
  lastConnected: string;
}

const STORAGE_CONNECTIONS_KEY = 'numixsheet_user_connections_';

export function getUserConnections(email: string): DbConnectionMeta[] {
  try {
    const key = `${STORAGE_CONNECTIONS_KEY}${email}`;
    const raw = getStorage().getItem(key);
    if (!raw) {
      const defaults: DbConnectionMeta[] = [
        {
          id: 'conn-default-1',
          name: 'PostgreSQL Production Replica',
          engine: 'PostgreSQL',
          host: 'db-replica.cloud.internal',
          port: 5432,
          dbName: 'production_sales',
          username: 'replica_reader',
          status: 'active',
          latencyMs: 12,
          lastConnected: 'Just now',
        },
        {
          id: 'conn-default-2',
          name: 'Snowflake Analytics DW',
          engine: 'Snowflake',
          host: 'xy12345.us-east-1.snowflakecomputing.com',
          port: 443,
          dbName: 'ANALYTICS_DB',
          username: 'analytics_svc',
          status: 'active',
          latencyMs: 38,
          lastConnected: '2 mins ago',
        },
      ];
      getStorage().setItem(key, JSON.stringify(defaults));
      return defaults;
    }
    return JSON.parse(raw) as DbConnectionMeta[];
  } catch {
    return [];
  }
}

export function saveUserConnection(email: string, conn: DbConnectionMeta): void {
  const key = `${STORAGE_CONNECTIONS_KEY}${email}`;
  const existing = getUserConnections(email);
  const idx = existing.findIndex((c) => c.id === conn.id);
  if (idx >= 0) {
    existing[idx] = conn;
  } else {
    existing.unshift(conn);
  }
  getStorage().setItem(key, JSON.stringify(existing));
}

export function deleteUserConnection(email: string, id: string): void {
  const key = `${STORAGE_CONNECTIONS_KEY}${email}`;
  const existing = getUserConnections(email).filter((c) => c.id !== id);
  getStorage().setItem(key, JSON.stringify(existing));
}
